import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { delay, finalize } from 'rxjs/operators';
import { DebugDelay } from 'src/app/shared/debug/debug.delay';
import { TypedForm } from 'src/app/shared/forms/typed.form';
import { SortOrder } from 'src/app/shared/types/sort.order';
import { GetQualitySurveysCommand } from 'src/app/surveys/quality-survey/commands/get.quality.surveys.command';
import { QualitySurveysService } from 'src/app/surveys/quality-survey/quality.surveys.service';
import { QualitySurveysViewModel } from 'src/app/surveys/quality-survey/viewmodels/quality.surveys.viewmodel';

@Component({
  selector: 'fit-quality-surveys',
  templateUrl: './quality.surveys.component.html'
})
export class QualitySurveysComponent implements OnDestroy {

  busyLoading = false;

  filterForm = new FilterForm();
  sortOrders = new Map<string, SortOrder>();
  page = 1;
  pageSize = 25;
  sortBy = 'CreatedDate';
  sortOrder = SortOrder.descending;

  viewmodel: QualitySurveysViewModel;
  destroyed = new Subject();

  constructor(private dashboardService: QualitySurveysService,
    private router: Router,
    private title: Title,
    @Inject(DOCUMENT) private document: Document) {
    this.sortOrders.set('CreatedDate', SortOrder.none);
    this.sortOrders.set('SubService', SortOrder.none);
    this.sortOrders.set('Status', SortOrder.none);
    this.sortOrders.set('Age', SortOrder.none);
    this.sortOrders.set('Comment', SortOrder.none);
    this.sortOrders.set('Service', SortOrder.none);
    this.sortOrders.set('ValueAssessment', SortOrder.none);
  }

  get canRefresh() {
    return !this.busyLoading;
  }

  get pageSizeButtonText(): string {
    return `Page size: ${this.pageSize}`;
  }

  ngOnInit() {
    this.title.setTitle("Survey Dashboard");
    this.load();
  }

  load() {
    this.busyLoading = true;

    const command = new GetQualitySurveysCommand(
      this.page,
      this.pageSize,
      this.filterForm.subService.typedValue,
      this.sortBy,
      this.sortOrder);

    this.dashboardService.getQualitySurveysForUser(command)
      .pipe(delay(DebugDelay.value), finalize(() => this.busyLoading = false))
      .subscribe((data) => {
        this.viewmodel = new QualitySurveysViewModel(data);
      });
    
  }

  runSearch() {
    this.page = 1;
    this.load();
  }

  submitSearch(args: KeyboardEvent) {
    if (args.key === "Enter") {
      this.runSearch();
    }
  }

  clearSubService() {
    this.filterForm.subService.reset();
  }

  clearSearch() {
    this.filterForm.reset({ isPassive: false });
    this.runSearch();
  }

  pageChange(pageNumber: number) {
    this.page = pageNumber;
    this.document.body.scrollIntoView(true);
    this.load();
  }

  setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.runSearch();
  }

  setSortOrder(args: [string, SortOrder]) {

    this.sortOrders.forEach((v, k, m) => {
      if (k === args[0]) {
        m.set(k, args[1]);
      }
      else {
        m.set(k, SortOrder.none);
      }
    });

    this.sortBy = args[0];
    this.sortOrder = args[1];
    this.page = 1;

    this.load();
  }

  viewSurvey(surveyId: string) {
    this.router.navigate(["surveys/quality/survey", surveyId]);
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}

class FilterForm extends TypedForm {
  subService = this.addTypedControl<string>("subService");
}
